import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, MapPin, Link as LinkIcon, Calendar, UserPlus, UserMinus, MessageCircle, Copy, Check, Shield, VolumeX, Volume2, Coins, ChevronDown, ChevronUp, Info, Loader2, Star, Music, Play, Pause, Heart, Disc3, TrendingUp, Settings2 } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useAppContext } from '../hooks/useAppContext';
import { useFollows, useFollowers, useFollowMutation } from '../hooks/useFollows';
import { useNostrPublish, getLocalEvents } from '../hooks/useNostrPublish';
import { useBCHAddress } from '../hooks/useBCHAddress';
import { useBCHPrice } from '../hooks/useBCHPrice';
import PostCard from '../components/PostCard';
import UserBadges, { useFirstSeen, reportSeen } from '../components/UserBadges';
import { useProfilePin } from '../hooks/usePinnedPost';
import { BCHLogo } from '../components/Layout';
import { REFERRAL_KIND, REFERRAL_DRTAG } from '../lib/referral';
import { useTrustScore } from '../hooks/useTrustScore';
import {
  TRUST_LEVEL_UI, TRUST_LEVELS, getTrustLevelName,
  POSITIVE_SIGNALS, UNVERIFIED_LEVEL_CAP, type TrustLevel,
} from '../lib/trustScore';
import { isFavorite, toggleFavorite } from '../lib/favorites';
import { useRadioPlayer } from '../contexts/RadioPlayerContext';
import { RADIO_TRACK_KIND, RADIO_TRACK_TAG, formatDuration, type RadioTrack } from '../lib/radio';
import { FLAG_KIND, BAN_TAG, UNBAN_TAG, isBanned, addLocalBan, removeLocalBan } from '../lib/communityFlag';
import { OPERATOR_PUBKEY } from '../lib/platform';
import { CreatorSupportPanel } from './CreatorSupportPage';
import { deduplicateEdits } from '../lib/utils';
import CreatorDashboard from '../components/CreatorDashboard';
import CirculationBadge, { CirculationBadgeInline } from '../components/CirculationBadge';
import ActivityBadges from '../components/ActivityBadges';
import { useBusinessProfile } from '../hooks/useBusinessProfile';
import { useBusinessVerification } from '../hooks/useBusinessVerification';
import VerifiedBusinessBadge from '../components/VerifiedBusinessBadge';
import { useVerifiedHandle } from '../hooks/useNip05Verified';
import { categoryLabel, BUSINESS_DAYS } from '../lib/business';
import { useBCHPodcast, usePodcastFollowerCount } from '../hooks/usePodcast';
import { podcastCategoryLabel, podcastPath } from '../lib/podcast';
import { Store, Globe as GlobeIcon, Phone, Mail, Clock, Image as ImageIcon, Mic2 } from 'lucide-react';
import PayBusinessButton from '../components/PayBusinessButton';

// ── Business profile (kind 30499) ────────────────────────────────
// Shown for any account that has flipped on its Business Profile.
// Read-only; the "Accepts BCH" signal comes straight from the
// business's own signed event.
//
// Split deliberately in two: a compact strip in the profile header
// (identity + Pay) and the full listing in its own tab. The listing has
// a lot in it — hours, gallery, contact details — and rendering all of
// it inline pushed the person's actual posts far below the fold.
/**
 * Compact business strip for the profile header.
 *
 * The full listing (hours, gallery, contact details, description) lives in
 * the Business tab — see `BusinessProfilePanel`. Keeping only identity and
 * the Pay button up here stops a storefront from burying the person's
 * posts, while still making it obvious at a glance that this is a business
 * and still allowing a one-tap payment.
 */
function BusinessProfileStrip({ pubkey, onViewDetails }: { pubkey: string; onViewDetails: () => void }) {
  const { business } = useBusinessProfile(pubkey);
  const verification = useBusinessVerification(pubkey);
  if (!business) return null;

  return (
    <div className="mt-3 flex items-center gap-2 rounded-xl border border-primary/25 bg-primary/[0.04] px-3 py-2">
      <div className="p-1.5 rounded-lg bg-primary/10 shrink-0">
        <Store className="w-4 h-4 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <h3 className="text-sm font-bold truncate">{business.name || 'Business'}</h3>
          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/15 text-primary shrink-0">
            Business
          </span>
          <VerifiedBusinessBadge businessPubkey={pubkey} state={verification} size="sm" />
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="truncate">{categoryLabel(business.category)}</span>
          <button
            onClick={onViewDetails}
            className="text-primary hover:underline font-medium shrink-0 no-navigate"
          >
            Details
          </button>
        </div>
      </div>
      {business.bch && (
        <div className="shrink-0 no-navigate">
          <PayBusinessButton
            recipientPubkey={pubkey}
            address={business.bch}
            businessName={business.name}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Compact podcast strip for the profile header.
 *
 * Deliberately tiny: identity, follower count, and a link to the show. The
 * podcast itself — episodes, player, discussion, analytics — lives on its
 * own page at /podcasts/<pubkey>, so a person's normal Nostr profile is
 * never turned into a podcast dashboard.
 */
function PodcastProfileStrip({ pubkey }: { pubkey: string }) {
  const { podcast } = useBCHPodcast(pubkey);
  const { count: followerCount } = usePodcastFollowerCount(pubkey);
  if (!podcast) return null;

  return (
    <Link
      to={podcastPath(pubkey)}
      className="mt-3 flex items-center gap-2.5 rounded-xl border border-primary/25 bg-primary/[0.04] px-3 py-2 hover:border-primary/50 transition-colors no-navigate group"
    >
      {podcast.image ? (
        <img
          src={podcast.image}
          alt=""
          className="w-10 h-10 rounded-lg object-cover border border-border shrink-0"
        />
      ) : (
        <div className="p-1.5 rounded-lg bg-primary/10 shrink-0">
          <Mic2 className="w-4 h-4 text-primary" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-bold truncate group-hover:text-primary transition-colors">
            {podcast.name}
          </h3>
          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/15 text-primary shrink-0">
            Podcast
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground truncate">
          {podcastCategoryLabel(podcast.category)}
          {followerCount > 0 && ` · ${followerCount} follower${followerCount === 1 ? '' : 's'}`}
        </p>
      </div>
      <span className="shrink-0 text-[11px] font-semibold text-primary">Listen</span>
    </Link>
  );
}

/**
 * The full business listing, rendered in its own profile tab so it has
 * room to breathe instead of competing with the bio and support panels.
 */
function BusinessProfilePanel({ pubkey }: { pubkey: string }) {
  const { business, loading } = useBusinessProfile(pubkey);
  const verification = useBusinessVerification(pubkey);

  if (loading && !business) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="text-center py-16">
        <Store className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
        <p className="text-muted-foreground text-sm">No business profile.</p>
      </div>
    );
  }

  const website = business.website;
  const safeWebsite = website && /^https?:\/\//i.test(website)
    ? website
    : website ? `https://${website}` : '';
  const locationLabel = business.onlineOnly ? 'Online only' : business.location;
  // Only render gallery images served over http(s) — never data:/javascript: etc.
  const safeGallery = (business.gallery || []).filter(u => /^https?:\/\//i.test(u)).slice(0, 8);

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 rounded-lg bg-primary/10">
          <Store className="w-4 h-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold truncate">{business.name || 'Business'}</h3>
            <VerifiedBusinessBadge businessPubkey={pubkey} state={verification} size="sm" />
          </div>
          <span className="text-[11px] text-muted-foreground">{categoryLabel(business.category)}</span>
        </div>
        {business.bch && (
          <div className="shrink-0 no-navigate">
            <PayBusinessButton
              recipientPubkey={pubkey}
              address={business.bch}
              businessName={business.name}
            />
          </div>
        )}
      </div>

      {business.tagline && (
        <p className="text-sm font-medium">{business.tagline}</p>
      )}
      {business.description && (
        <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{business.description}</p>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs">
        {locationLabel && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <MapPin className="w-3 h-3" /> {locationLabel}
          </span>
        )}
        {safeWebsite && (
          <a href={safeWebsite} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
            <GlobeIcon className="w-3 h-3" /> Website
          </a>
        )}
        {business.phone && (
          <a href={`tel:${business.phone.replace(/[^\d+]/g, '')}`} className="flex items-center gap-1 text-primary hover:underline">
            <Phone className="w-3 h-3" /> {business.phone}
          </a>
        )}
        {business.email && (
          <a href={`mailto:${business.email}`} className="flex items-center gap-1 text-primary hover:underline">
            <Mail className="w-3 h-3" /> Email
          </a>
        )}
        {business.bch && (
          <span className="flex items-center gap-1 text-primary font-medium">
            <BCHLogo size={12} /> Accepts BCH{business.acceptsCashtokens ? ' + CashTokens' : ''}
          </span>
        )}
        {verification.status === 'VERIFIED' && (
          <a
            href="https://bchlocal.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-emerald-500 hover:underline font-medium"
          >
            <MapPin className="w-3 h-3" /> View on BCH Local
          </a>
        )}
      </div>

      {business.hours && Object.keys(business.hours).length > 0 && (
        <div className="mt-4 pt-3 border-t border-border">
          <div className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground mb-1.5">
            <Clock className="w-3 h-3" /> Hours
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-0.5 text-[11px]">
            {BUSINESS_DAYS.filter(d => business.hours?.[d.key]).map(d => (
              <div key={d.key} className="flex justify-between gap-2">
                <span className="text-muted-foreground">{d.label}</span>
                <span className="font-medium truncate">{business.hours![d.key]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {safeGallery.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border">
          <div className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground mb-1.5">
            <ImageIcon className="w-3 h-3" /> Photos
          </div>
          {/* With a whole tab to work in, photos can be a proper grid
              rather than a cramped horizontal scroller. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {safeGallery.map((url, i) => (
              <a
                key={`${url}-${i}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <img
                  src={url}
                  alt=""
                  loading="lazy"
                  className="aspect-square w-full rounded-lg object-cover border border-border hover:border-primary/50 transition-colors"
                />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RepliesTab({ pubkey }: { pubkey: string }) {
  const { events: replies, loading } = useNostrQuery({
    filters: [{ kinds: [1], authors: [pubkey], limit: 50 }],
    enabled: true,
    allRelays: true,
  });

  // Filter to only events that have an 'e' tag (indicating they're replies)
  const replyPosts = replies.filter(ev =>
    ev.tags.some(t => t[0] === 'e')
  ).sort((a, b) => b.created_at - a.created_at);

  if (loading && replyPosts.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (replyPosts.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground text-sm">No replies yet.</p>
      </div>
    );
  }

  return (
    <div>
      {replyPosts.map(event => (
        <PostCard key={event.id} event={event} showReplyTo={true} />
      ))}
    </div>
  );
}

function OperatorBanButton({ pubkey }: { pubkey: string }) {
  const { publish } = useNostrPublish();
  const [banned, setBanned] = useState(() => isBanned(pubkey));
  const [confirming, setConfirming] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handleBan = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      await publish({
        kind: FLAG_KIND,
        content: 'Site-wide ban by operator',
        tags: [
          ['t', BAN_TAG],
          ['p', pubkey],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      addLocalBan(pubkey);
      setBanned(true);
      setConfirming(false);
    } catch {}
    setProcessing(false);
  };

  const handleUnban = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      await publish({
        kind: FLAG_KIND,
        content: 'Unbanned by operator',
        tags: [
          ['t', UNBAN_TAG],
          ['p', pubkey],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      removeLocalBan(pubkey);
      setBanned(false);
    } catch {}
    setProcessing(false);
  };

  if (banned) {
    return (
      <button
        onClick={handleUnban}
        disabled={processing}
        className="flex items-center gap-1 px-3 py-2 rounded-full border border-red-500/30 bg-red-500/10 text-red-500 text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
        title="Unban this user (operator)"
      >
        {processing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
        Banned — Unban
      </button>
    );
  }

  if (confirming) {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={handleBan}
          disabled={processing}
          className="px-3 py-2 rounded-full bg-red-500 text-white text-xs font-medium hover:bg-red-600 disabled:opacity-50 transition-colors"
        >
          {processing ? 'Banning...' : 'Confirm Ban'}
        </button>
        <button
          onClick={() => setConfirming(false)}
          className="px-3 py-2 rounded-full border border-border text-xs font-medium hover:bg-muted transition-colors"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className="p-2 rounded-full border border-red-500/20 hover:bg-red-500/10 text-red-500/70 hover:text-red-500 transition-colors"
      title="Ban this user site-wide (operator)"
    >
      <Shield className="w-4 h-4" />
    </button>
  );
}

function MusicTab({ pubkey }: { pubkey: string }) {
  const radio = useRadioPlayer();
  const { profile } = useNostrProfile(pubkey);
  const artistName = profile?.display_name || profile?.name || pubkey.slice(0, 8);

  // Filter radio queue to this artist's tracks
  const tracks = useMemo(() =>
    radio.queue
      .filter(t => t.submittedBy === pubkey)
      .sort((a, b) => b.submittedAt - a.submittedAt),
    [radio.queue, pubkey]
  );

  const totalPlays = tracks.length; // Placeholder — real play counts would come from events
  const totalLikes = tracks.reduce((sum, t) => sum + (radio.likeCounts.get(t.id) || 0), 0);
  const totalTips = tracks.reduce((sum, t) => sum + (radio.tipCounts.get(t.id) || 0), 0);

  if (tracks.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <Music className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
        <p className="text-sm font-medium text-muted-foreground">No music uploaded yet</p>
        <p className="text-xs text-muted-foreground mt-1">Tracks uploaded to BCH Radio will appear here.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Stats bar */}
      <div className="flex items-center gap-4 px-4 py-3 border-b border-border bg-muted/30">
        <div className="text-center">
          <p className="text-sm font-bold">{tracks.length}</p>
          <p className="text-[10px] text-muted-foreground">Songs</p>
        </div>
        <div className="text-center">
          <p className="text-sm font-bold">{totalLikes}</p>
          <p className="text-[10px] text-muted-foreground">Likes</p>
        </div>
        {totalTips > 0 && (
          <div className="text-center">
            <p className="text-sm font-bold text-emerald-500">{totalTips.toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">Sats earned</p>
          </div>
        )}
      </div>

      {/* Track list */}
      {tracks.map((track, i) => {
        const qIdx = radio.queue.findIndex(t => t.id === track.id);
        const isActive = qIdx === radio.currentIndex;
        const isPlaying = isActive && radio.isPlaying;
        const likeCount = radio.likeCounts.get(track.id) || 0;

        return (
          <Link
            key={track.id}
            to={`/radio?track=${track.id}`}
            className={`flex items-center gap-3 px-4 py-3 border-b border-border transition-colors ${
              isActive ? 'bg-primary/5 border-l-2 border-l-primary' : 'hover:bg-muted/30'
            }`}
          >
            {/* Cover art */}
            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden">
              {track.coverArt ? (
                <img src={track.coverArt} alt="" className="w-full h-full object-cover" />
              ) : (
                <Disc3 className={`w-5 h-5 text-muted-foreground/30 ${isPlaying ? 'animate-spin' : ''}`} style={isPlaying ? { animationDuration: '3s' } : undefined} />
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${isActive ? 'text-primary' : ''}`}>{track.title}</p>
              <div className="flex items-center gap-2 mt-0.5">
                {track.genre && <span className="text-[10px] text-muted-foreground">{track.genre}</span>}
                {track.duration > 0 && <span className="text-[10px] text-muted-foreground">{formatDuration(track.duration)}</span>}
                {likeCount > 0 && (
                  <span className="flex items-center gap-0.5 text-[10px] text-red-500">
                    <Heart className="w-2.5 h-2.5" /> {likeCount}
                  </span>
                )}
              </div>
            </div>

            {/* Play button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (qIdx >= 0) radio.playTrack(qIdx);
              }}
              className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors shrink-0"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
          </Link>
        );
      })}
    </div>
  );
}

// ── Profile Tab Preferences ─────────────────────────────────────────
// Tabs the user can toggle on/off. "posts" and "dashboard" are always visible.
type ToggleableTab = 'about' | 'replies' | 'videos' | 'music' | 'following' | 'followers' | 'likes';
const TOGGLEABLE_TABS: { id: ToggleableTab; label: string }[] = [
  { id: 'about', label: 'About' },
  { id: 'replies', label: 'Replies' },
  { id: 'videos', label: 'Videos' },
  { id: 'music', label: 'Music' },
  { id: 'following', label: 'Following' },
  { id: 'followers', label: 'Followers' },
  { id: 'likes', label: 'Likes' },
];
// About keeps its own fixed slot (with icon) at the head of the tab strip,
// so it's excluded from the generic inline row below.
const INLINE_TOGGLEABLE_TABS = TOGGLEABLE_TABS.filter(t => t.id !== 'about');
const TAB_PREFS_KEY = 'bchnostr:profile-tabs';
// Bumped when a new tab joins the toggle menu, so existing users don't
// silently lose it just because their saved prefs predate it.
const TAB_PREFS_MIGRATION_KEY = 'bchnostr:profile-tabs-migrated-about';
const DEFAULT_VISIBLE: ToggleableTab[] = ['about', 'replies', 'videos', 'music', 'following', 'followers'];
// Bumped when Videos joined the toggle menu, so existing users get the new
// tab instead of silently missing it because their saved prefs predate it.
const TAB_PREFS_MIGRATION_VIDEOS = 'bchnostr:profile-tabs-migrated-videos';

function loadTabPrefs(): Set<ToggleableTab> {
  try {
    const raw = localStorage.getItem(TAB_PREFS_KEY);
    if (raw) {
      const visible = new Set<ToggleableTab>(JSON.parse(raw));
      // About was always-on before it became toggleable — keep it on for
      // anyone whose stored prefs were written before that change.
      if (!localStorage.getItem(TAB_PREFS_MIGRATION_KEY)) {
        visible.add('about');
        localStorage.setItem(TAB_PREFS_MIGRATION_KEY, '1');
        localStorage.setItem(TAB_PREFS_KEY, JSON.stringify([...visible]));
      }
      // Same for Videos, which arrived with BCH Video.
      if (!localStorage.getItem(TAB_PREFS_MIGRATION_VIDEOS)) {
        visible.add('videos');
        localStorage.setItem(TAB_PREFS_MIGRATION_VIDEOS, '1');
        localStorage.setItem(TAB_PREFS_KEY, JSON.stringify([...visible]));
      }
      return visible;
    }
  } catch {}
  return new Set(DEFAULT_VISIBLE);
}

function saveTabPrefs(visible: Set<ToggleableTab>) {
  try { localStorage.setItem(TAB_PREFS_KEY, JSON.stringify([...visible])); } catch {}
}

/**
 * Normalise a `/profile/:pubkey` route param to a 64-char hex pubkey.
 *
 * Accepts raw hex (what most of the app links with), plus `npub` and
 * `nprofile` bech32 forms. Returns undefined for anything unusable so the
 * page can render its not-found state instead of throwing mid-render.
 */
function normalizeProfileParam(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const value = raw.trim();

  // Already hex.
  if (/^[0-9a-f]{64}$/i.test(value)) return value.toLowerCase();

  // NIP-19 bech32 forms.
  if (/^(npub|nprofile)1/i.test(value)) {
    try {
      const decoded = nip19.decode(value);
      if (decoded.type === 'npub' && typeof decoded.data === 'string') {
        return decoded.data;
      }
      if (decoded.type === 'nprofile') {
        const d = decoded.data as { pubkey?: string };
        if (d?.pubkey) return d.pubkey;
      }
    } catch {
      return undefined;
    }
  }

  return undefined;
}

export default function ProfilePage() {
  const { pubkey: pubkeyParam } = useParams<{ pubkey: string }>();
  // The route param may be raw hex OR a NIP-19 npub/nprofile — most of the app
  // links with hex, but some surfaces (Radio, the business directory) link with
  // an npub. Everything below assumes hex, and nip19.npubEncode() throws
  // ("hex characters in even length") on a bech32 input, so normalise once here.
  const pubkey = useMemo(() => normalizeProfileParam(pubkeyParam), [pubkeyParam]);
  const { user, isMuted, mute, unmute } = useAppContext();
  const { profile: relayProfile, loading: profileLoading } = useNostrProfile(pubkey);

  // Keep last known good relay profile — never flash back to null once we have data
  const [stableRelayProfile, setStableRelayProfile] = useState<any>(null);
  useEffect(() => {
    if (relayProfile) setStableRelayProfile(relayProfile);
  }, [relayProfile]);
  const bestRelayProfile = relayProfile || stableRelayProfile;

  // A paid @bchnostr.com handle, resolved from the authoritative directory.
  // Used as a fallback when the profile's kind 0 has been re-published with
  // a blank name / nip05 — the badge is still valid, so we still show it.
  const verifiedHandle = useVerifiedHandle(pubkey);
  // The @bchnostr.com handle without the domain, for use as a display name.
  const verifiedHandleName = verifiedHandle ? verifiedHandle.split('@')[0] : '';

  // For your own profile, merge local user data (from Settings save) with relay data
  // This ensures profile picture/banner show up immediately after saving
  // Use relay data as the base and only override with non-empty local values
  const isOwnProfile = user?.pubkey === pubkey;
  const profile = isOwnProfile && user ? {
    ...bestRelayProfile,
    name: user.name?.trim() || bestRelayProfile?.name,
    display_name: user.name?.trim() || bestRelayProfile?.display_name,
    picture: user.picture?.trim() || bestRelayProfile?.picture,
    banner: user.banner?.trim() || bestRelayProfile?.banner,
    about: user.about?.trim() || bestRelayProfile?.about,
    nip05: user.nip05?.trim() || bestRelayProfile?.nip05,
  } : bestRelayProfile;
  const { follows: myFollows } = useFollows(user?.pubkey);
  const { follows: theirFollows } = useFollows(pubkey);
  const { followerCount, followers, loading: followersLoading } = useFollowers(pubkey);
  const { publish } = useNostrPublish();
  const { mutate: followMutate, ready: followsReady } = useFollowMutation();
  const [activeTab, setActiveTab] = useState<'posts' | 'replies' | 'music' | 'following' | 'followers' | 'likes' | 'dashboard' | 'business' | 'about'>('posts');
  const [visibleTabs, setVisibleTabs] = useState<Set<ToggleableTab>>(loadTabPrefs);
  const [showTabSettings, setShowTabSettings] = useState(false);
  const tabSettingsRef = useRef<HTMLDivElement>(null);
  const { price: bchPrice } = useBCHPrice();
  // Drives whether the Business tab exists at all. Shares the same query
  // as the strip and panel (useNostrQuery dedupes), so this costs nothing.
  const { business: businessProfile } = useBusinessProfile(pubkey);
  const hasBusiness = !!businessProfile;

  // If the Business tab is open but the profile turns out not to be a
  // business (removed, or a slow relay never returns it), fall back to
  // Posts rather than leaving the content area blank.
  useEffect(() => {
    if (activeTab === 'business' && !hasBusiness) setActiveTab('posts');
  }, [activeTab, hasBusiness]);

  // Close tab settings dropdown when clicking outside
  useEffect(() => {
    if (!showTabSettings) return;
    const handleClick = (e: MouseEvent) => {
      if (tabSettingsRef.current && !tabSettingsRef.current.contains(e.target as Node)) {
        setShowTabSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showTabSettings]);

  const toggleTab = useCallback((tab: ToggleableTab) => {
    setVisibleTabs(prev => {
      const next = new Set(prev);
      if (next.has(tab)) {
        next.delete(tab);
        // If the currently active tab was hidden, switch to posts
        if (activeTab === tab) setActiveTab('posts');
      } else {
        next.add(tab);
      }
      saveTabPrefs(next);
      return next;
    });
  }, [activeTab]);

  const [copied, setCopied] = useState<'npub' | 'bch' | null>(null);
  const { address: bchAddress } = useBCHAddress(pubkey);

  // "Joined" date — from firstSeen (oldest known event timestamp).
  // We no longer use _ts (kind 0 created_at) because that's just when
  // the profile was last edited, not when the account was created.
  // firstSeen is improved over time as more events load, and a "deep scan"
   // queries relays for the user's actual oldest event.
  const firstSeen = useFirstSeen(pubkey);
  const joinedTs = firstSeen > 0 ? firstSeen : 0;
  const accountDate = joinedTs > 0 ? new Date(joinedTs * 1000) : null;

  // Recruiter badge — count confirmed referral events that credit this pubkey
  // NOTE: We query by kind + d-tag only (not #p) because many relays don't
  // support filtering on multiple tag types (#d AND #p) simultaneously.
  // Client-side filtering handles the rest.
  const { events: allReferralEvents } = useNostrQuery({
    filters: pubkey ? [{ kinds: [REFERRAL_KIND], '#d': [REFERRAL_DRTAG], limit: 500 }] : [],
    enabled: !!pubkey,
  });
  const recruiterCount = useMemo(() => {
    if (!pubkey) return 0;
    const seen = new Set<string>();
    for (const ev of allReferralEvents) {
      // Check if this referral credits the current profile's pubkey
      const refTag = ev.tags.find(t => t[0] === 'ref')?.[1];
      const pTag = ev.tags.find(t => t[0] === 'p')?.[1];
      if ((refTag === pubkey || pTag === pubkey) && ev.pubkey !== pubkey) {
        seen.add(ev.pubkey);
      }
    }
    return seen.size;
  }, [allReferralEvents, pubkey]);

  // Trust score — used for community standing indicator + own reputation card
  const trust = useTrustScore(pubkey);

  // Pinned post
  const { pinnedEventId, pin: pinPost, unpin: unpinPost } = useProfilePin(pubkey);

  const isFollowing = pubkey ? myFollows.includes(pubkey) : false;
  const [isFav, setIsFav] = useState(() => pubkey ? isFavorite(pubkey, user?.pubkey) : false);

  // Sync favorite state when pubkey changes
  useEffect(() => {
    setIsFav(pubkey ? isFavorite(pubkey, user?.pubkey) : false);
  }, [pubkey, user?.pubkey]);

  const handleToggleFavorite = () => {
    if (!pubkey || !user) return;
    const nowFav = toggleFavorite(pubkey, user.pubkey);
    setIsFav(nowFav);
  };

  // ── Reply detection helper ──
  // Returns true if an event is a reply (not a top-level post).
  // Quote posts (only 'mention'-marked e-tags or only 'q' tags) are NOT replies.
  const isReply = useCallback((e: any): boolean => {
    if (!e.tags || e.tags.length === 0) return false;
    if (e.kind === 1068) return false; // polls are top-level
    if (e.kind === 1111) return true; // NIP-22 comments always scoped to parent context
    const eTags = e.tags.filter((t: any) => t[0] === 'e');
    // Check for a-tag with 'root' marker (legacy blog comments)
    const hasARootTag = e.tags.some((t: any) => t[0] === 'a' && t[3] === 'root');
    if (hasARootTag) return true;
    if (eTags.length === 0) return false;
    // If every e-tag is marked 'mention', it's a quote/reference, not a reply
    const nonMentionETags = eTags.filter((t: any) => t[3] !== 'mention');
    return nonMentionETags.length > 0;
  }, []);

  // Paginated posts — load older posts as user scrolls
  const [postUntil, setPostUntil] = useState<number | undefined>(undefined);
  const [allPosts, setAllPosts] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // ── Outbox: this user's own write relays (NIP-65 kind 10002) ──────
  // Some people publish through clients (e.g. "PosterChan AI") that write to
  // relays this app doesn't read by default, so their posts never show up on
  // their profile even though they're active. We fetch their relay list and
  // query their write relays too, so the profile isn't blank.
  const { events: relayListEvents } = useNostrQuery({
    filters: pubkey ? [{ kinds: [10002], authors: [pubkey], limit: 1 }] : [],
    enabled: !!pubkey,
    allRelays: true,
  });
  // Well-known public relays to fall back to when we can't learn an author's
  // own write relays (they published no NIP-65 kind 10002 list). Widely-used
  // general-purpose relays carry most people's notes, so querying them keeps
  // the profile from looking blank just because the author posts through a
  // client whose relays we don't read by default. Kept small to avoid
  // ballooning every profile query.
  const FALLBACK_DISCOVERY_RELAYS = useMemo(() => [
    'wss://relay.damus.io',
    'wss://relay.nostr.band',
    'wss://purplepag.es',
  ], []);

  const authorWriteRelays = useMemo(() => {
    const ev = relayListEvents.find(e => e.pubkey === pubkey);
    // No relay list for this author yet — fall back to broad discovery relays
    // so their posts can still surface. (relayListEvents can resolve later, at
    // which point this recomputes to their real write relays.)
    if (!ev) return FALLBACK_DISCOVERY_RELAYS;
    const relays: string[] = [];
    for (const t of ev.tags) {
      // ["r", <url>] (both read+write) or ["r", <url>, "write"]
      if (t[0] !== 'r' || !t[1]) continue;
      const marker = t[2];
      if (marker === undefined || marker === 'write') {
        let url = t[1].trim();
        if (/^wss?:\/\//i.test(url)) {
          url = url.replace(/\/+$/, '');
          relays.push(url);
        }
      }
    }
    // If the list had no usable write relays, still fall back to discovery.
    if (relays.length === 0) return FALLBACK_DISCOVERY_RELAYS;
    // Cap so a user with a huge relay list can't balloon every query.
    return [...new Set(relays)].slice(0, 6);
  }, [relayListEvents, pubkey, FALLBACK_DISCOVERY_RELAYS]);

  const PAGE_SIZE = 50;
  const { events: posts, loading: postsLoading } = useNostrQuery({
    filters: pubkey ? [{ kinds: [1], authors: [pubkey], limit: PAGE_SIZE, ...(postUntil ? { until: postUntil } : {}) }] : [],
    enabled: !!pubkey,
    allRelays: true,
    extraRelays: authorWriteRelays,
  });

  // Accumulate posts across pages — but only for the current profile pubkey
  useEffect(() => {
    if (posts.length > 0) {
      // Guard: ignore results that don't belong to the current profile
      const profilePosts = posts.filter(e => e.pubkey === pubkey);
      if (profilePosts.length === 0) return;

      // Feed timestamps into the first-seen cache so account age improves
      // as the user scrolls and loads more posts
      if (pubkey) {
        for (const e of profilePosts) {
          reportSeen(pubkey, e.created_at);
        }
      }

      setAllPosts(prev => {
        // If the prev posts are for a different user, start fresh
        const prevBelongsToCurrent = prev.length === 0 || prev.every(e => e.pubkey === pubkey);
        const base = prevBelongsToCurrent ? prev : [];
        const seen = new Set(base.map((e: any) => e.id));
        const newPosts = profilePosts.filter(e => !seen.has(e.id));
        if (newPosts.length === 0) return base;
        return [...base, ...newPosts].sort((a, b) => b.created_at - a.created_at);
      });
      if (posts.length < PAGE_SIZE) setHasMore(false);
      setLoadingMore(false);
    } else if (!postsLoading && postUntil) {
      setHasMore(false);
      setLoadingMore(false);
    }
  }, [posts, postsLoading, pubkey]);

  // Reset when pubkey changes — runs before the new query results arrive
  useEffect(() => {
    setAllPosts([]);
    setPostUntil(undefined);
    setHasMore(true);
    setLoadingMore(false);
  }, [pubkey]);

  const loadMorePosts = useCallback(() => {
    if (!hasMore || loadingMore || allPosts.length === 0) return;
    setLoadingMore(true);
    // Set until to 1 second before the oldest post we have
    const oldestTs = allPosts[allPosts.length - 1].created_at;
    setPostUntil(oldestTs - 1);
  }, [hasMore, loadingMore, allPosts]);

  // Infinite scroll — observe sentinel element
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && !postsLoading) {
          loadMorePosts();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, postsLoading, loadMorePosts]);

  const [followToggling, setFollowToggling] = useState(false);
  const handleFollow = async () => {
    if (!user || !pubkey || followToggling) return;
    // Don't hard-block on followsReady — the backup-based ready state
    // is fast enough for mobile. Just warn if truly not ready.
    if (!followsReady) return;
    setFollowToggling(true);
    try {
      await followMutate(isFollowing ? 'unfollow' : 'follow', pubkey);
    } catch (err) {
      console.error('Follow toggle failed:', err);
    } finally {
      setFollowToggling(false);
    }
  };

  if (!pubkey) return null;

  return (
    <div>
      {/* Banner */}
      <div className="h-32 sm:h-44 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent relative">
        {profile?.banner && (
          <img src={profile.banner} alt="" className="w-full h-full object-cover" />
        )}
      </div>

      {/* Banned warning — visible to operator */}
      {pubkey && isBanned(pubkey) && user?.pubkey === OPERATOR_PUBKEY && (
        <div className="px-4 py-2.5 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-500 shrink-0" />
          <p className="text-xs font-semibold text-red-500">This user is banned site-wide. Their content is hidden for all visitors.</p>
        </div>
      )}

      {/* Profile Header */}
      <div className="px-4 pb-4 border-b border-border">
        <div className="flex items-end justify-between -mt-12 sm:-mt-16 mb-3">
          <div className="relative">
            {profile?.picture ? (
              <img
                src={profile.picture}
                alt=""
                className="w-20 h-20 sm:w-28 sm:h-28 rounded-full border-4 border-background object-cover"
              />
            ) : (
              <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full border-4 border-background bg-muted flex items-center justify-center">
                <User className="w-10 h-10 text-muted-foreground" />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-14 sm:mt-18">
            {!isOwnProfile && user && (
              <>
                <button
                  onClick={handleToggleFavorite}
                  className={`p-2 rounded-full border transition-colors ${
                    isFav
                      ? 'border-amber-500/30 bg-amber-500/10 text-amber-500 hover:bg-amber-500/20'
                      : 'border-border hover:bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                  title={isFav ? 'Remove from Favorites (stop notifications)' : 'Add to Favorites (notify me when they post)'}
                >
                  <Star className={`w-4 h-4 ${isFav ? 'fill-amber-500' : ''}`} />
                </button>
                <Link to={`/messages?to=${pubkey}`} className="p-2 rounded-full border border-border hover:bg-muted" title="Message">
                  <MessageCircle className="w-4 h-4" />
                </Link>
                {pubkey && isMuted(pubkey) ? (
                  <button
                    onClick={() => unmute(pubkey)}
                    className="p-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 text-emerald-600 transition-colors"
                    title="Unmute user"
                  >
                    <Volume2 className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => pubkey && mute(pubkey)}
                    className="p-2 rounded-full border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title="Mute user"
                  >
                    <VolumeX className="w-4 h-4" />
                  </button>
                )}
                {/* Operator Ban/Unban button */}
                {user?.pubkey === OPERATOR_PUBKEY && pubkey && pubkey !== OPERATOR_PUBKEY && (
                  <OperatorBanButton pubkey={pubkey} />
                )}
                <button
                  onClick={handleFollow}
                  disabled={followToggling}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium transition-all active:scale-95 select-none touch-manipulation min-h-[40px] ${
                    followToggling
                      ? 'border border-border text-muted-foreground opacity-70'
                      : isFollowing
                        ? 'border border-border hover:border-red-500 hover:text-red-500 active:bg-red-500/10'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80'
                  }`}
                >
                  {followToggling ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {isFollowing ? 'Unfollowing...' : 'Following...'}</>
                  ) : isFollowing ? (
                    <><UserMinus className="w-4 h-4" /> Unfollow</>
                  ) : (
                    <><UserPlus className="w-4 h-4" /> Follow</>
                  )}
                </button>
              </>
            )}
            {isOwnProfile && (
              <Link to="/settings" className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted">
                Edit Profile
              </Link>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold">
              {profile?.display_name || profile?.name || verifiedHandleName || 'Anonymous'}
            </h1>
            {pubkey && <UserBadges pubkey={pubkey} size="md" recruiterCount={recruiterCount} />}
          </div>
          {(profile?.nip05 || verifiedHandle) && (
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Shield className="w-3.5 h-3.5" /> {profile?.nip05 || verifiedHandle}
            </p>
          )}

          {/* Account age indicator — show "Joined" with the oldest known date */}
          {accountDate && (
            <p
              className="text-sm text-muted-foreground flex items-center gap-1"
              title={`Earliest known activity: ${accountDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`}
            >
              <Calendar className="w-3.5 h-3.5" />
              Joined {accountDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </p>
          )}

          {/* Option 3 — Community standing indicator (all profiles) */}
          {!trust.loading && trust.level > 0 && (
            <CommunityStandingBadge level={trust.level} />
          )}

          {/* Circulation badge — shown for users with community wallets */}
          <CirculationBadgeInline pubkey={pubkey} />

          {/* npub + BCH address / bquest username */}
          <div className="mt-1.5 space-y-1">
            {/* npub */}
            <button
              onClick={() => {
                let npub = pubkey;
                try { npub = nip19.npubEncode(pubkey); } catch { /* fall back to hex */ }
                navigator.clipboard.writeText(npub);
                setCopied('npub');
                setTimeout(() => setCopied(null), 2000);
              }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors group"
              title="Copy npub"
            >
              <span className="font-mono truncate max-w-[220px] sm:max-w-[320px]">
                {(() => {
                  // Never let an unexpected pubkey take down the whole page.
                  try {
                    const npub = nip19.npubEncode(pubkey);
                    return npub.slice(0, 16) + '...' + npub.slice(-6);
                  } catch {
                    return pubkey.slice(0, 16) + '...';
                  }
                })()}
              </span>
              {copied === 'npub' ? <Check className="w-3 h-3 text-emerald-500 shrink-0" /> : <Copy className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />}
            </button>

            {/* BCH address or bquest username */}
            {bchAddress && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(bchAddress);
                  setCopied('bch');
                  setTimeout(() => setCopied(null), 2000);
                }}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors group"
                title="Copy BCH address"
              >
                <BCHLogo size={12} />
                <span className="font-mono truncate max-w-[220px] sm:max-w-[320px]">
                  {(() => {
                    const clean = bchAddress.replace(/^bitcoincash:/i, '');
                    return clean.length > 24 ? clean.slice(0, 12) + '...' + clean.slice(-8) : clean;
                  })()}
                </span>
                {copied === 'bch' ? <Check className="w-3 h-3 text-emerald-500 shrink-0" /> : <Copy className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />}
              </button>
            )}
          </div>
        </div>

        {profile?.about && (
          <p className="text-sm mt-2 whitespace-pre-wrap">{profile.about}</p>
        )}

        {/* Business identity strip — full listing lives in the Business tab */}
        {pubkey && (
          <BusinessProfileStrip
            pubkey={pubkey}
            onViewDetails={() => setActiveTab('business')}
          />
        )}

        {/* Podcast strip — driven purely by the show event, independent of
            any business profile, so any podcaster gets a link to their show. */}
        {pubkey && <PodcastProfileStrip pubkey={pubkey} />}

        {/* Creator Support — tip, membership, goal. This is the one block
            that stays: it's a call to action, and burying a "support this
            person" button behind a tab defeats the point of the app. */}
        {pubkey && (
          <div className="mt-3">
            <CreatorSupportPanel pubkey={pubkey} bchPrice={bchPrice} />
          </div>
        )}

        {/* Recognition badges render as bare chips here — the full cards
            (badges, circulation stats, private reputation) live in the
            About tab. Four stacked cards between the bio and the post
            list was the reason this header felt crowded. */}
        {pubkey && (
          <div className="mt-2.5">
            <ActivityBadges pubkey={pubkey} variant="inline" />
          </div>
        )}

        {/* Website / BCH-enabled moved to the About tab — they were another
            row competing with the follower counts directly below. */}

        <div className="flex items-center gap-4 mt-3 text-sm">
          <button
            onClick={() => setActiveTab(activeTab === 'following' ? 'posts' : 'following')}
            className={`rounded-md px-2 py-1 -mx-2 transition-colors ${activeTab === 'following' ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}`}
          >
            <strong>{theirFollows.length}</strong> <span className="text-muted-foreground">Following</span>
          </button>
          <button
            onClick={() => setActiveTab(activeTab === 'followers' ? 'posts' : 'followers')}
            className={`rounded-md px-2 py-1 -mx-2 transition-colors ${activeTab === 'followers' ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}`}
          >
            <strong>{followersLoading ? '—' : followerCount}</strong> <span className="text-muted-foreground">Followers</span>
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`rounded-md px-2 py-1 -mx-2 transition-colors ${activeTab === 'posts' ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}`}
          >
            <strong>{allPosts.length > 0 ? allPosts.filter(e => !isReply(e)).length : posts.filter(e => !isReply(e)).length}{hasMore ? '+' : ''}</strong> <span className="text-muted-foreground">Posts</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        <div className="flex flex-1 overflow-x-auto scrollbar-hide">
          {/* Dashboard tab — own profile only, always shown */}
          {isOwnProfile && (
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap px-2.5 ${
                activeTab === 'dashboard'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <TrendingUp className="w-3.5 h-3.5 inline mr-0.5" />Dashboard
            </button>
          )}
          {/* About — recognition, circulation, reputation, links.
              Toggleable from the gear menu on your own profile. */}
          {visibleTabs.has('about') && (
            <button
              onClick={() => setActiveTab('about')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap px-2.5 ${
                activeTab === 'about'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Info className="w-3.5 h-3.5 inline mr-0.5" />About
            </button>
          )}
          {/* Business — only for accounts that have a business profile, so
              it never shows as an empty tab on a personal account. */}
          {hasBusiness && (
            <button
              onClick={() => setActiveTab('business')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap px-2.5 ${
                activeTab === 'business'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Store className="w-3.5 h-3.5 inline mr-0.5" />Business
            </button>
          )}
          {/* Posts — always visible */}
          <button
            onClick={() => setActiveTab('posts')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap px-2 ${
              activeTab === 'posts'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Posts
          </button>
          {/* Toggleable tabs */}
          {INLINE_TOGGLEABLE_TABS.filter(t => visibleTabs.has(t.id)).map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap px-2 ${
                activeTab === id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {id === 'music' ? <><Music className="w-3.5 h-3.5 inline mr-0.5" />Music</> : label}
              {id === 'following' && (
                <span className="ml-1 text-xs text-muted-foreground">({theirFollows.length})</span>
              )}
              {id === 'followers' && !followersLoading && (
                <span className="ml-1 text-xs text-muted-foreground">({followerCount})</span>
              )}
            </button>
          ))}
        </div>
        {/* Tab settings gear — own profile only */}
        {isOwnProfile && (
          <div className="relative shrink-0 border-b-2 border-transparent" ref={tabSettingsRef}>
            <button
              onClick={() => setShowTabSettings(v => !v)}
              className={`px-2.5 py-3 transition-colors ${
                showTabSettings ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Customize tabs"
            >
              <Settings2 className="w-4 h-4" />
            </button>
            {showTabSettings && (
              <div className="absolute right-0 top-full mt-1 z-30 w-48 rounded-xl border border-border bg-card shadow-xl py-1.5 animate-in fade-in slide-in-from-top-1 duration-150">
                <p className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Show tabs</p>
                {TOGGLEABLE_TABS.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => toggleTab(id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/50 transition-colors"
                  >
                    <span className={visibleTabs.has(id) ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
                    <div className={`w-8 h-[18px] rounded-full transition-colors relative ${
                      visibleTabs.has(id) ? 'bg-primary' : 'bg-muted'
                    }`}>
                      <div className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-all ${
                        visibleTabs.has(id) ? 'left-[16px]' : 'left-[2px]'
                      }`} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Business listing — hours, photos, contact details */}
      {activeTab === 'business' && pubkey && (
        <BusinessProfilePanel pubkey={pubkey} />
      )}

      {/* About — the detail cards that used to stack in the header */}
      {activeTab === 'about' && pubkey && (
        <div className="p-4 space-y-3">
          {/* Full recognition card, with counts and per-badge detail */}
          <ActivityBadges
            pubkey={pubkey}
            title={isOwnProfile ? 'Your Recognition' : 'Recognition'}
            showEmptyState={isOwnProfile}
          />

          {/* Community circulation stats. Renders nothing without a
              community wallet, so it simply won't appear for most people. */}
          <CirculationBadge pubkey={pubkey} />

          {/* Private reputation — own profile only, never shown to others */}
          {isOwnProfile && !trust.loading && (
            <ReputationCard trust={trust} />
          )}

          {/* Links that were previously squeezed into the header */}
          {(profile?.website || profile?.lud16) && (
            <div className="rounded-xl border border-border bg-card p-3 space-y-1.5">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Links
              </h3>
              {profile?.website && (
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-primary hover:underline break-all"
                >
                  <LinkIcon className="w-3.5 h-3.5 shrink-0" />
                  {profile.website.replace(/^https?:\/\//, '')}
                </a>
              )}
              {profile?.lud16 && (
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <BCHLogo size={14} /> BCH enabled
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Posts — merge local events so own posts appear instantly */}
      {activeTab === 'posts' && (() => {
        const localPosts = pubkey ? getLocalEvents().filter(e => e.pubkey === pubkey && (e.kind === 1 || e.kind === 1068)) : [];
        const combined = [...localPosts, ...allPosts];
        const seen = new Set<string>();
        const dedupedPosts = deduplicateEdits(
          combined
            .filter(e => { if (seen.has(e.id)) return false; seen.add(e.id); return true; })
            .filter(e => !isReply(e))
        );

        // Separate pinned post from the rest
        const pinnedPostEvent = pinnedEventId ? dedupedPosts.find(e => e.id === pinnedEventId) : null;
        const regularPosts = pinnedPostEvent ? dedupedPosts.filter(e => e.id !== pinnedEventId) : dedupedPosts;

        return postsLoading && dedupedPosts.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        ) : dedupedPosts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-sm">No posts yet.</p>
          </div>
        ) : (
          <>
            {/* Pinned post (always first) */}
            {pinnedPostEvent && (
              <div key={`pinned-${pinnedPostEvent.id}`} className="border-b-2 border-primary/20">
                <PostCard
                  event={pinnedPostEvent}
                  isPinned
                  onUnpin={isOwnProfile ? () => unpinPost() : undefined}
                />
              </div>
            )}
            {/* Regular posts */}
            {regularPosts.map(event => (
              <PostCard
                key={event.id}
                event={event}
                onPin={isOwnProfile && !pinnedEventId ? () => pinPost(event.id) : undefined}
              />
            ))}
            {/* Load more sentinel */}
            {hasMore && (
              <div ref={loadMoreRef} className="flex items-center justify-center py-6">
                {loadingMore ? (
                  <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                ) : (
                  <button
                    onClick={loadMorePosts}
                    className="text-xs text-primary hover:underline"
                  >
                    Load older posts
                  </button>
                )}
              </div>
            )}
            {!hasMore && dedupedPosts.length > 10 && (
              <div className="text-center py-6">
                <p className="text-xs text-muted-foreground">You've reached the beginning</p>
              </div>
            )}
          </>
        );
      })()}

      {/* Replies tab */}
      {activeTab === 'replies' && pubkey && (
        <RepliesTab pubkey={pubkey} />
      )}

      {/* Music tab */}
      {activeTab === 'music' && pubkey && (
        <MusicTab pubkey={pubkey} />
      )}

      {/* Following tab */}
      {activeTab === 'following' && pubkey && (
        <FollowingTab pubkey={pubkey} follows={theirFollows} />
      )}

      {/* Followers tab */}
      {activeTab === 'followers' && pubkey ? (
        <FollowersTab pubkey={pubkey} relayFollowers={followers} relayLoading={followersLoading} />
      ) : null}

      {/* Likes tab placeholder */}
      {activeTab === 'likes' && (
        <div className="text-center py-16">
          <p className="text-muted-foreground text-sm">Likes coming soon.</p>
        </div>
      )}

      {/* Creator Dashboard — own profile only */}
      {activeTab === 'dashboard' && isOwnProfile && pubkey && (
        <CreatorDashboard pubkey={pubkey} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Option 3 — Community Standing Badge (visible on all profiles)
// ═══════════════════════════════════════════════════════════════════

function CommunityStandingBadge({ level }: { level: TrustLevel }) {
  const [showInfo, setShowInfo] = useState(false);
  const ui = TRUST_LEVEL_UI[level];
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    if (!showInfo) return;
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showInfo]);

  return (
    <div className="relative inline-block" ref={popoverRef}>
      <button
        onClick={() => setShowInfo(!showInfo)}
        className={`inline-flex items-center gap-1 text-sm ${ui.color} hover:opacity-80 transition-opacity`}
        title={`Community standing: ${ui.name}`}
      >
        <span className="text-xs">{ui.icon}</span>
        <span className="font-medium text-xs">{ui.name}</span>
      </button>

      {showInfo && (
        <div className="absolute left-0 top-full mt-1.5 z-30 w-64 bg-popover border border-border rounded-xl shadow-xl p-4 space-y-2.5">
          {/* Header */}
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base ${ui.bgColor} border`}>
              {ui.icon}
            </div>
            <div>
              <p className={`text-sm font-bold ${ui.color}`}>{ui.name}</p>
              <p className="text-[10px] text-muted-foreground">Community Standing</p>
            </div>
          </div>

          {/* Description */}
          <p className="text-xs text-muted-foreground leading-relaxed">
            {ui.description}
          </p>

          {/* What this means */}
          <div className="space-y-1 pt-1 border-t border-border">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">What this means</p>
            {level === 0 && (
              <p className="text-[11px] text-muted-foreground">New accounts have limited posting to prevent spam. Keep participating to unlock full access.</p>
            )}
            {level === 1 && (
              <p className="text-[11px] text-muted-foreground">This user has been active in the community. Can boost posts, create polls, and participate fully.</p>
            )}
            {level === 2 && (
              <p className="text-[11px] text-muted-foreground">A consistently helpful community member. Their spam reports carry extra weight and they're eligible for ambassador roles.</p>
            )}
            {level === 3 && (
              <p className="text-[11px] text-muted-foreground">A long-standing community pillar with the highest trust level. Eligible for all advanced features.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
//  Option 2 — Private Reputation Card (own profile only)
// ═══════════════════════════════════════════════════════════════════

function ReputationCard({ trust }: { trust: import('../hooks/useTrustScore').TrustScoreResult }) {
  const [expanded, setExpanded] = useState(false);
  const ui = TRUST_LEVEL_UI[trust.level];
  const signals = trust.signals;

  // If capped, show what the user *would* be and what they need to do
  const isCapped = trust.isCapped;
  const rawUi = isCapped ? TRUST_LEVEL_UI[trust.rawLevel] : null;

  // Calculate progress to next level
  // When capped, show progress toward the cap threshold (verification needed)
  const currentLevelInfo = TRUST_LEVELS[trust.level];
  const nextLevel = trust.level < 3 ? trust.level + 1 : null;
  const nextLevelInfo = nextLevel !== null ? TRUST_LEVELS[nextLevel] : null;

  let progressPercent = 100;
  let pointsToNext = 0;
  if (isCapped) {
    // When capped, the progress bar is full since the user has earned enough points
    // but is being held back by verification
    progressPercent = 100;
    pointsToNext = 0;
  } else if (nextLevelInfo) {
    const rangeStart = currentLevelInfo.minScore;
    const rangeEnd = nextLevelInfo.minScore;
    const progressInRange = trust.score - rangeStart;
    const totalRange = rangeEnd - rangeStart;
    progressPercent = Math.min(100, Math.max(0, Math.round((progressInRange / totalRange) * 100)));
    pointsToNext = rangeEnd - trust.score;
  }

  // Build signal breakdown items
  const breakdown = useMemo(() => {
    if (!signals) return [];
    const items: { label: string; value: number; points: number; max?: number }[] = [];

    const ageDays = signals.firstSeenTimestamp > 0
      ? Math.floor((Date.now() / 1000 - signals.firstSeenTimestamp) / 86400)
      : 0;

    items.push({
      label: 'Account age',
      value: ageDays,
      points: Math.min(ageDays, 365) * POSITIVE_SIGNALS.perDayAccountExists,
      max: 365,
    });
    items.push({
      label: 'Active days',
      value: signals.activeDays,
      points: Math.min(signals.activeDays, 365) * POSITIVE_SIGNALS.perActiveDay,
      max: 365,
    });
    items.push({
      label: 'Posts',
      value: signals.postCount,
      points: Math.min(signals.postCount, 500) * POSITIVE_SIGNALS.perPost,
      max: 500,
    });
    items.push({
      label: 'Replies',
      value: signals.meaningfulReplyCount,
      points: Math.min(signals.meaningfulReplyCount, 500) * POSITIVE_SIGNALS.perMeaningfulReply,
      max: 500,
    });
    items.push({
      label: 'Tips sent',
      value: signals.tipsSentCount,
      points: Math.min(signals.tipsSentCount, 200) * POSITIVE_SIGNALS.perTipSent,
      max: 200,
    });
    items.push({
      label: 'Tips received',
      value: signals.tipsReceivedCount,
      points: Math.min(signals.tipsReceivedCount, 200) * POSITIVE_SIGNALS.perTipReceived,
      max: 200,
    });
    if (signals.isVerified) {
      items.push({
        label: 'Verified account',
        value: 1,
        points: POSITIVE_SIGNALS.verifiedAccount,
      });
    }
    if (signals.crowdfundContributions > 0) {
      items.push({
        label: 'Crowdfund pledges',
        value: signals.crowdfundContributions,
        points: Math.min(signals.crowdfundContributions, 50) * POSITIVE_SIGNALS.perCrowdfundContribution,
        max: 50,
      });
    }

    return items.sort((a, b) => b.points - a.points);
  }, [signals]);

  // Tips for improving score
  const tips = useMemo(() => {
    const t: string[] = [];
    if (!signals) return t;
    if (isCapped) {
      // Verification is the #1 priority when capped
      t.push('Verify your account to unlock your earned trust level');
    } else if (!signals.isVerified) {
      t.push('Verify your account for an instant +10 boost (and to unlock higher levels)');
    }
    if (signals.meaningfulReplyCount < 20) t.push('Reply to others\' posts — replies earn 2x points');
    if (signals.tipsSentCount < 10) t.push('Send tips to show community support');
    if (signals.crowdfundContributions === 0) t.push('Contribute to a crowdfund campaign');
    if (t.length === 0) t.push('Keep posting, replying, and engaging — you\'re doing great!');
    return t.slice(0, 3);
  }, [signals, isCapped]);

  return (
    <div className="mt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full rounded-xl border ${ui.bgColor} p-3 transition-all hover:opacity-90`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg border ${ui.bgColor}`}>
              {ui.icon}
            </div>
            <div className="text-left">
              <div className="flex items-center gap-1.5">
                <span className={`text-sm font-bold ${ui.color}`}>{ui.name}</span>
                <span className="text-[10px] text-muted-foreground bg-muted rounded-full px-1.5 py-0.5">
                  Only you can see this
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {isCapped
                  ? `Verify to unlock ${getTrustLevelName(trust.rawLevel)}`
                  : nextLevelInfo
                  ? `${pointsToNext} point${pointsToNext !== 1 ? 's' : ''} to ${getTrustLevelName(nextLevel! as TrustLevel)}`
                  : 'Highest level reached'
                }
              </p>
            </div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>

        {/* Progress bar (always visible) */}
        <div className="mt-2.5 h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${ui.barColor} transition-all duration-700 ease-out`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        {nextLevelInfo && (
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-muted-foreground">{currentLevelInfo.minScore}</span>
            <span className="text-[10px] text-muted-foreground">{nextLevelInfo.minScore}</span>
          </div>
        )}
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="mt-2 rounded-xl border border-border bg-card p-4 space-y-4">
          {/* Verification cap notice */}
          {isCapped && rawUi && (
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-base">{rawUi.icon}</span>
                <p className="text-xs font-bold text-amber-600 dark:text-amber-400">
                  You've earned {rawUi.name} — verify to unlock it!
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Your score qualifies you for <strong className={rawUi.color}>{rawUi.name}</strong>, but
                unverified accounts are capped at <strong>{TRUST_LEVEL_UI[UNVERIFIED_LEVEL_CAP].name}</strong>.
                Verify your account to unlock your full trust level and all its perks.
              </p>
            </div>
          )}

          {/* Signal breakdown */}
          {breakdown.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">What counts</h4>
              <div className="space-y-1.5">
                {breakdown.filter(b => b.points > 0).map(item => (
                  <div key={item.label} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{item.label}</span>
                        <span className="font-mono font-medium text-foreground">+{item.points}</span>
                      </div>
                      {item.max && (
                        <div className="mt-0.5 h-1 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${ui.barColor}/60 transition-all`}
                            style={{ width: `${Math.min(100, (item.value / item.max) * 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tips to grow / max level congrats / verification cap nudge */}
          {(trust.level < 3 || isCapped) && tips.length > 0 ? (
            <div className="space-y-1.5 pt-2 border-t border-border">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <Info className="w-3 h-3" /> How to grow
              </h4>
              {tips.map((tip, i) => (
                <p key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className="text-primary mt-0.5 shrink-0">&bull;</span>
                  {tip}
                </p>
              ))}
            </div>
          ) : trust.level === 3 ? (
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1.5">
                <span>{TRUST_LEVEL_UI[3].icon}</span>
                You've reached the highest trust level. Thank you for being a pillar of the BCH community.
              </p>
            </div>
          ) : null}

          {/* Level descriptions - compact roadmap */}
          <div className="space-y-1.5 pt-2 border-t border-border">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Levels</h4>
            <div className="grid grid-cols-2 gap-1.5">
              {([0, 1, 2, 3] as TrustLevel[]).map(lvl => {
                const lvlUi = TRUST_LEVEL_UI[lvl];
                const isCurrent = lvl === trust.level;
                const isPast = lvl < trust.level;
                // Show "earned but locked" state for levels above cap that the user has earned
                const isEarnedButLocked = isCapped && lvl > trust.level && lvl <= trust.rawLevel;
                return (
                  <div
                    key={lvl}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs transition-colors ${
                      isCurrent
                        ? `${lvlUi.bgColor} font-semibold ${lvlUi.color}`
                        : isEarnedButLocked
                        ? 'border-amber-500/30 bg-amber-500/5 text-amber-600 dark:text-amber-400'
                        : isPast
                        ? 'border-border bg-muted/30 text-muted-foreground'
                        : 'border-border/50 text-muted-foreground/60'
                    }`}
                  >
                    <span>{lvlUi.icon}</span>
                    <span>{lvlUi.shortName}</span>
                    {isCurrent && <span className="ml-auto text-[9px] opacity-70">You</span>}
                    {isEarnedButLocked && <span className="ml-auto text-[9px]" title="Verify to unlock">🔒</span>}
                    {isPast && !isEarnedButLocked && <span className="ml-auto text-[9px] text-emerald-500">&#10003;</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function FollowersTab({ pubkey, relayFollowers, relayLoading }: { pubkey: string; relayFollowers: string[]; relayLoading: boolean }) {
  const [apiFollowers, setApiFollowers] = useState<string[]>([]);
  const [apiDone, setApiDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

   // Fetch followers directly from relay that indexes social graphs
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    (async () => {
      try {
        // Query relay for kind 3 with #p filter to find followers
        const found: string[] = [];
        await new Promise<void>((resolve) => {
          const ws = new WebSocket('wss://relay.nostr.net');
          const subId = 'flw_' + Math.random().toString(36).slice(2, 8);
          const timeout = setTimeout(() => { try { ws.close(); } catch {} resolve(); }, 8000);

          ws.onopen = () => {
            ws.send(JSON.stringify(['REQ', subId, {
              kinds: [3],
              '#p': [pubkey],
              limit: 500,
            }]));
          };
          ws.onmessage = (msg) => {
            try {
              const data = JSON.parse(msg.data);
              if (data[0] === 'EVENT' && data[1] === subId) {
                const ev = data[2];
                if (ev?.pubkey && typeof ev.pubkey === 'string' && ev.pubkey.length === 64) {
                  // Verify the event actually has a p-tag for our target
                  const hasTag = ev.tags?.some((t: any) => t[0] === 'p' && t[1] === pubkey);
                  if (hasTag && !found.includes(ev.pubkey)) {
                    found.push(ev.pubkey);
                  }
                }
              }
              if (data[0] === 'EOSE' && data[1] === subId) {
                clearTimeout(timeout);
                try { ws.close(); } catch {}
                resolve();
              }
            } catch {}
          };
          ws.onerror = () => { clearTimeout(timeout); resolve(); };
          ws.onclose = () => { clearTimeout(timeout); resolve(); };
        });

        if (found.length > 0) {
          setApiFollowers(found.slice(0, 200));
        }
      } catch (e) {
        console.warn('[FollowersTab] Follower fetch failed:', e);
      }
      setApiDone(true);
    })();
  }, [pubkey]);

  // Merge relay followers + API followers, deduplicate, ensure all are strings
  const allFollowers = [...new Set([
    ...relayFollowers.filter(pk => typeof pk === 'string' && pk.length === 64),
    ...apiFollowers,
  ])];

  // Still loading if we have no data AND at least one source is still pending
  const stillSearching = allFollowers.length === 0 && (relayLoading || !apiDone);

  if (stillSearching) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (allFollowers.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground text-sm">No followers found.</p>
        <p className="text-xs text-muted-foreground mt-1">Follower data depends on relay support. Some followers may not appear.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {allFollowers.map(followerPubkey => (
        <FollowerRow key={followerPubkey} pubkey={followerPubkey} />
      ))}
    </div>
  );
}

function FollowingTab({ pubkey, follows }: { pubkey: string; follows: string[] }) {
  if (follows.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground text-sm">Not following anyone yet.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {follows.map(followPubkey => (
        <FollowerRow key={followPubkey} pubkey={followPubkey} />
      ))}
    </div>
  );
}

function FollowerRow({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);

  // Safely extract string fields from profile (some profiles have malformed data)
  const displayName = typeof profile?.display_name === 'string' ? profile.display_name : '';
  const name = typeof profile?.name === 'string' ? profile.name : '';
  const nip05 = typeof profile?.nip05 === 'string' ? profile.nip05 : '';
  const about = typeof profile?.about === 'string' ? profile.about : '';
  const picture = typeof profile?.picture === 'string' ? profile.picture : '';

  return (
    <Link to={`/profile/${pubkey}`} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
      {picture ? (
        <img src={picture} alt="" className="w-10 h-10 rounded-full object-cover" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <User className="w-5 h-5 text-primary/60" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="font-semibold text-sm truncate">{displayName || name || pubkey.slice(0, 12) + '...'}</p>
          <UserBadges pubkey={pubkey} size="sm" />
        </div>
        {nip05 && (
          <p className="text-xs text-muted-foreground truncate">{nip05}</p>
        )}
        {about && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{about}</p>}
      </div>
    </Link>
  );
}

